/**
 * A certificate, shown where the reader already is.
 *
 * ── Why this replaced window.open ─────────────────────────────────────────────────────────
 *
 * Three screens opened a certificate by handing a presigned S3 URL to `window.open(url,
 * '_blank')`. That is a tab, not a view, and it costs the reader the thing they were doing:
 *
 *   an approver checking a queue        loses their place in it, and comes back to a reloaded
 *                                       list with their scroll position gone
 *   a register of forty certifications  becomes forty tabs, one per document glanced at
 *   a phone                             switches apps entirely, because a presigned PDF opens
 *                                       in the OS viewer rather than beside the page
 *
 * And it is the one navigation a popup blocker stops. `window.open` is only exempt while the
 * browser can attribute it to a click, and this one happens AFTER an await on getCertificateUrl
 * — so on a slow presign the gesture has expired and the call is blocked silently. The reader
 * presses View Certificate and nothing at all happens.
 *
 * ── One component for all three screens ───────────────────────────────────────────────────
 *
 * All Certifications, Certification Approvals and the Reimbursement queue each had their own
 * copy of the open-and-handle-the-error dance. They now each render this. Three copies of a
 * viewer is three places for the PDF case to be handled differently.
 *
 * ── Why the document type is read from the NAME ───────────────────────────────────────────
 *
 * A presigned URL carries the object key plus a long query string, and the key is not always
 * suffixed. The stored document name is what the employee uploaded and is the only reliable
 * statement of what the file is. When it says nothing, the iframe is the safe default: a browser
 * given an image in an iframe shows the image, whereas an <img> given a PDF shows a broken icon.
 *
 * ── What happens when the browser will not render it ──────────────────────────────────────
 *
 * Every state keeps a working way out. The frame is never the only route to the document: the
 * footer always carries "Open in a new tab" and a download, so a client that refuses to render a
 * PDF inline — iOS Safari historically, and any browser with a PDF plugin disabled — leaves the
 * reader one click from the file rather than looking at an empty rectangle.
 */

import React, { useCallback, useEffect, useRef } from 'react';
import './Certifications.css';

/**
 * What a page holds while a certificate is open.
 *
 * Exported so the three screens that show one all keep the same state shape — the alternative is
 * three near-identical inline types that drift the first time one of them needs another field.
 */
export interface ViewingCertificate {
  /** The presigned URL, already fetched by the caller. */
  url: string;
  /** The certification this document belongs to, for the heading. */
  title: string;
  /** The uploaded file's own name, which is what decides how it is rendered. */
  documentName?: string;
}

interface Props extends ViewingCertificate {
  onClose: () => void;
}

/** Extensions a browser renders as a picture. Everything else goes in the frame. */
const IMAGE_SUFFIXES = ['.png', '.jpg', '.jpeg', '.gif', '.webp', '.bmp', '.avif'];

export const isImageDocument = (documentName?: string): boolean => {
  const name = String(documentName || '').trim().toLowerCase();
  if (!name) return false;
  return IMAGE_SUFFIXES.some((suffix) => name.endsWith(suffix));
};

const CertificateViewerModal: React.FC<Props> = ({ url, title, documentName, onClose }) => {
  const closeRef = useRef<HTMLButtonElement>(null);

  /*
   * Escape closes it, and the close button takes focus when it opens.
   *
   * Both because this is a dialog that covers the page: without the first there is no keyboard
   * way out, and without the second a screen reader is left reading the list behind it.
   */
  useEffect(() => {
    const onKey = (event: KeyboardEvent) => {
      if (event.key === 'Escape') onClose();
    };
    document.addEventListener('keydown', onKey);
    closeRef.current?.focus();
    return () => document.removeEventListener('keydown', onKey);
  }, [onClose]);

  /*
   * A click on the backdrop closes; a click inside does not.
   *
   * Guarded on the target being the backdrop ITSELF rather than on a stopPropagation inside,
   * because the panel holds an iframe and a stray propagation stop around foreign content is the
   * kind of thing that swallows a click the document wanted.
   */
  const onBackdrop = useCallback(
    (event: React.MouseEvent<HTMLDivElement>) => {
      if (event.target === event.currentTarget) onClose();
    },
    [onClose]
  );

  const image = isImageDocument(documentName);

  return (
    <div
      className="cert-modal cert-modal--viewer"
      role="dialog"
      aria-modal="true"
      aria-label={`Certificate: ${title}`}
      onClick={onBackdrop}
    >
      <div className="cert-modal__panel cert-modal__panel--wide">
        <div className="cert-modal__head">
          <h2>{title}</h2>
          <button
            type="button"
            ref={closeRef}
            className="cert-modal__close"
            onClick={onClose}
            aria-label="Close the certificate"
          >
            ×
          </button>
        </div>

        <div className="cert-viewer">
          {image ? (
            <img className="cert-viewer__image" src={url} alt={`Certificate for ${title}`} />
          ) : (
            /*
             * `title` is what a screen reader announces for the frame, and it is required for
             * one — an unlabelled iframe is read out as "frame" and nothing else.
             */
            <iframe className="cert-viewer__frame" src={url} title={`Certificate for ${title}`} />
          )}
        </div>

        {/*
          * Always present, never conditional on the frame having failed.
          *
          * A frame that refuses to render gives no event to hang a fallback on — it simply draws
          * nothing — so the escape route cannot be something this component decides to show.
          */}
        <div className="cert-modal__actions cert-viewer__actions">
          {documentName && <span className="certs__muted cert-viewer__name">{documentName}</span>}
          <a className="certs__btn certs__btn--sm" href={url} target="_blank" rel="noopener noreferrer">
            Open in a new tab
          </a>
          <a
            className="certs__btn certs__btn--sm"
            href={url}
            download={documentName || undefined}
            target="_blank"
            rel="noopener noreferrer"
          >
            Download
          </a>
          <button type="button" className="certs__btn certs__btn--sm certs__btn--quiet" onClick={onClose}>
            Close
          </button>
        </div>
      </div>
    </div>
  );
};

export default CertificateViewerModal;
